import React from "react";
import { Box, IconButton, Tooltip, Typography } from "@mui/material";
import VisibilityIcon from "@mui/icons-material/Visibility";
import VisibilityOffIcon from "@mui/icons-material/VisibilityOff";
import DragIndicatorIcon from "@mui/icons-material/DragIndicator";
import EditIcon from "@mui/icons-material/Edit";
import CheckIcon from "@mui/icons-material/Check";
import CloseIcon from "@mui/icons-material/Close";
import { useSortable } from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import HPButton from "@components/common/Button";
import type { StackedManagedItem } from "@utils/stacked-bar-utils";

interface StackedItemRowProps {
  item: StackedManagedItem;
  isEditing: boolean;
  editValue: string;
  isDisabled: boolean;
  isDragDisabled: boolean;
  /** Segments never get a visibility control — every current Grid Value is required. */
  showVisibilityToggle: boolean;
  onToggleVisibility: (id: string) => void;
  onStartEdit: (item: StackedManagedItem) => void;
  onSaveEdit: () => void;
  onCancelEdit: () => void;
  onResetLabel: (id: string) => void;
  onEditValueChange: (value: string) => void;
}

/**
 * A single draggable Stacks/Segments item row: optional visibility toggle
 * (Stacks only), drag handle, label with the original dataset label kept as
 * secondary reference text when a display override exists, and inline label
 * editing.
 */
const StackedItemRow: React.FC<StackedItemRowProps> = ({
  item,
  isEditing,
  editValue,
  isDisabled,
  isDragDisabled,
  showVisibilityToggle,
  onToggleVisibility,
  onStartEdit,
  onSaveEdit,
  onCancelEdit,
  onResetLabel,
  onEditValueChange,
}) => {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } =
    useSortable({ id: item.id, disabled: isDragDisabled });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
  };

  const isHidden = showVisibilityToggle && !item.visible;

  return (
    <Box
      ref={setNodeRef}
      style={style}
      sx={{
        display: "flex",
        alignItems: "center",
        gap: 1,
        p: 1,
        border: "1px solid #e0e0e0",
        borderRadius: 1,
        backgroundColor: isHidden ? "#f5f5f5" : "white",
        opacity: isHidden ? 0.6 : 1,
        cursor: isDragging ? "grabbing" : "default",
      }}
    >
      {!isDragDisabled && !isDisabled && (
        <Box
          {...attributes}
          {...listeners}
          sx={{
            display: "flex",
            alignItems: "center",
            cursor: "grab",
            color: "#666",
            "&:active": { cursor: "grabbing" },
          }}
        >
          <DragIndicatorIcon fontSize="small" />
        </Box>
      )}

      {showVisibilityToggle && (
        <IconButton
          size="small"
          onClick={() => onToggleVisibility(item.id)}
          disabled={isDisabled}
          sx={{ flexShrink: 0 }}
        >
          {isHidden ? (
            <VisibilityOffIcon fontSize="small" />
          ) : (
            <VisibilityIcon fontSize="small" />
          )}
        </IconButton>
      )}

      {isEditing ? (
        <Box sx={{ flex: 1 }}>
          <input
            type="text"
            value={editValue}
            onChange={(e) => onEditValueChange(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === "Enter") onSaveEdit();
              if (e.key === "Escape") onCancelEdit();
            }}
            autoFocus
            className="w-full px-3 py-2 border border-[#D9D9D9] rounded-lg text-sm outline-none focus:border-[#1976d2] transition-colors"
          />
        </Box>
      ) : (
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Tooltip title={item.displayLabel} enterDelay={400} placement="top">
            <Typography
              variant="body2"
              sx={{
                overflow: "hidden",
                textOverflow: "ellipsis",
                whiteSpace: "nowrap",
                fontWeight: item.hasOverride ? 600 : 400,
              }}
            >
              {item.displayLabel}
            </Typography>
          </Tooltip>
          {item.hasOverride && (
            <Typography
              variant="caption"
              color="text.secondary"
              sx={{
                overflow: "hidden",
                textOverflow: "ellipsis",
                whiteSpace: "nowrap",
                display: "block",
              }}
            >
              Original: {item.originalLabel}
            </Typography>
          )}
        </Box>
      )}

      {isEditing ? (
        <>
          <IconButton size="small" onClick={onSaveEdit} color="primary">
            <CheckIcon fontSize="small" />
          </IconButton>
          <IconButton size="small" onClick={onCancelEdit}>
            <CloseIcon fontSize="small" />
          </IconButton>
        </>
      ) : (
        <>
          <IconButton
            size="small"
            onClick={() => onStartEdit(item)}
            disabled={isHidden || isDisabled}
          >
            <EditIcon fontSize="small" />
          </IconButton>
          {item.hasOverride && (
            <HPButton
              size="small"
              variant="text"
              onClick={() => onResetLabel(item.id)}
              disabled={isDisabled}
            >
              Reset
            </HPButton>
          )}
        </>
      )}
    </Box>
  );
};

export default StackedItemRow;
